'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import type { Product } from '@eliadmin/core/src/api/slugs/types/slug-detail-types';

import { useCategoryStore } from '@eliadmin/core/src/stores/category-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { VariantsProvider, useVariantsStore } from '@eliadmin/core/src/stores/variant-store';
import {
  getProductFilterVariants,
  getProductImage,
  getProductUrl,
} from '@eliadmin/core/src/utils/product-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { getProductPriceDisplay } from '@eliadmin/core/src/utils/product-price-utils';

import Image from '@eliadmin/core/src/components/image/image-wrapped';
import React from 'react';
import Link from '@eliadmin/core/src/components/link';

import { LuShoppingCart } from 'react-icons/lu';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

export type ProductCardProps = {
  product: Product;
};

function ProductCardInternal({ product }: ProductCardProps) {
  const templateSettings = useGlobalStore(s => s.templateSettings);
  const isWholesaler = useCustomerStore(s => s.me?.customer.is_wholesaler);
  const { t } = useTranslations();
  const filter = useCategoryStore(state => state.filter);
  const selectedValues = useVariantsStore(state => state.selectedValues);

  const filterVariants = getProductFilterVariants(product, filter);
  const image = getProductImage(templateSettings, product, filterVariants);
  const productUrl = getProductUrl(product, selectedValues);
  const priceDisplay = getProductPriceDisplay(product, {
    isWholesaler,
    priceWithoutVatFirst: templateSettings.product_detail?.price_without_vat_first?.enabled,
  });

  const formatPrice = (price: number) => {
    const [integerPart, decimalPart] = price.toFixed(2).split('.');
    const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    return `${formattedInteger}.${decimalPart}`;
  };

  return (
    <Link
      prefetch={false}
      href={new URL(productUrl, 'https://rsmoto.sk/').toString()}
      className={cn(
        'ea-landing-page__product-card',
        'flex w-full flex-col items-start justify-start gap-4 p-6',
      )}
    >
      <Image
        src={image?.url ?? '/images/placeholder.png'}
        alt={resolveImageAlt(image, product?.name)}
        width={0}
        height={0}
        className="aspect-square h-auto w-full object-contain object-bottom p-4"
        sizes="(max-width: 640px) 100vw, (max-width: 768px) 50vw, (max-width: 1024px) 33vw, 25vw"
      />

      <div className="flex w-full items-center justify-between gap-4">
        <div className="flex flex-col items-start justify-start gap-1 sm:gap-2 lg:gap-4">
          <p className="line-clamp-1 text-sm font-semibold text-black sm:text-base lg:text-lg">
            {product.name}
          </p>
          {priceDisplay.priceOnRequest ? (
            <strong className="text-base font-semibold text-primary">{t('text2278')}</strong>
          ) : (
            <>
              {priceDisplay.showMocVocPrices && priceDisplay.mocPrice != null && (
                <span className="text-xs text-muted-foreground">
                  {t('text1926')} {formatPrice(priceDisplay.mocPrice)} €
                </span>
              )}
              <strong className="text-base font-semibold tracking-tighter text-primary sm:text-lg lg:text-xl">
                {formatPrice(priceDisplay.current!)} €
              </strong>
              {priceDisplay.secondary != null && priceDisplay.showMocVocPrices && (
                <span className="text-xs text-muted-foreground">
                  {t('text1925')} {formatPrice(priceDisplay.secondary)} €{' '}
                  {priceDisplay.secondaryIncludesVat ? t('text1122') : t('text443')}
                </span>
              )}
            </>
          )}
        </div>
        <div className="flex max-h-12 min-h-12 min-w-12 max-w-12 items-center justify-center bg-primary text-white transition-all hover:bg-black sm:max-h-14 sm:min-h-14 sm:min-w-14 sm:max-w-14">
          <LuShoppingCart size={20} />
        </div>
      </div>
    </Link>
  );
}

export function ProductCard(props: ProductCardProps) {
  return (
    <VariantsProvider state={{ product: props.product }}>
      <ProductCardInternal {...props} />
    </VariantsProvider>
  );
}
